import React, { useEffect, useMemo, useState } from "react";
import { Building2, Save, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const FIELDS = [
  ["business_name", "Business / legal name", "Shown in Terms & Privacy", "Acme LLC dba Powerball Oracle"],
  ["support_email", "Support email", "Reply-To on every email + contact on legal pages", "support@yourdomain.com"],
  ["governing_law", "Governing law", "Country + state/province", "Texas, USA"],
  ["address", "Business address (optional)", "Footer of legal pages", "123 Main St, Austin, TX 78701"],
];

export function BusinessDetails({ adminKey }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const H = useMemo(() => ({ headers: { "X-Admin-Key": adminKey } }), [adminKey]);

  useEffect(() => {
    api.get("/admin/business", H).then((r) => setForm({ ...r.data, support_email: r.data.support_email || "", address: r.data.address || "" })).catch(() => setForm({}));
  }, [H]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/business", { ...form, support_email: form.support_email || null, address: form.address || null }, H);
      setForm({ ...data, support_email: data.support_email || "", address: data.address || "" });
      toast.success("Business details saved — legal pages and email Reply-To updated");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;
  return (
    <div className="mt-8 bg-surface border border-white/5 rounded-xl p-5 sm:p-6" data-testid="business-details">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps flex items-center gap-2 text-parchment/60"><Building2 className="w-3 h-3" strokeWidth={1.5} /> Business details</div>
          <p className="font-body text-xs text-parchment/50 mt-1">Feeds the Terms, Privacy and Refund pages and the Reply-To address on trial, reminder and result-alert emails.{!form.support_email && <span className="text-oracle"> No support email set yet — customers currently can't reply to you.</span>}</p>
        </div>
        <Button data-testid="business-save-btn" onClick={save} disabled={saving} className="bg-oracle text-ink hover:bg-[#FFC033] label-caps">
          {saving ? <><RefreshCw className="w-3 h-3 mr-2 animate-spin" /> Saving…</> : <><Save className="w-3 h-3 mr-2" strokeWidth={1.5} /> Save</>}
        </Button>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, hint, ph]) => (
          <div key={k} className="space-y-1.5">
            <Label className="label-caps">{label}</Label>
            <Input data-testid={`business-${k.replace(/_/g, "-")}`} value={form[k] || ""} placeholder={ph} type={k === "support_email" ? "email" : "text"}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="bg-ink border-white/10 text-parchment font-body focus-visible:ring-oracle" />
            <div className="font-body text-[10px] text-parchment/40">{hint}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
